import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { onDestroy } from 'svelte';
import { MAX_RAMPS, MAX_STOPS_PER_RAMP, MIN_RAMPS } from '../../../../features/lut-editor/lut-editor-model.ts';
import { createLutFromColorRamp2d } from '../../../../features/lut-editor/lut-editor-painter.ts';

import {
	addRamp,
	addStop,
	moveRamp,
	moveStop,
	removeRamp,
	removeStop,
	renderColorRamp2dToPixels,
	reorderRamps,
	updateRamp,
	updateStopAlpha,
	updateStopColor
} from '../../../../features/lut-editor/lut-editor-runtime.ts';

import { colorToHex, parseHexColor, uid } from '../../../../features/pipeline/pipeline-model.ts';
import { getLanguage, subscribeLanguageChange, t } from '../../i18n.ts';

import {
	createPointerReorderListController,
	syncReorderDropIndicators
} from '../../interactions/reorder-list.ts';

import Button from '../svelte-button.svelte';
import PreviewPane from './lut-editor-preview-pane.svelte';
import RampSection from './lut-editor-ramp-section.svelte';
import StopSection from './lut-editor-stop-section.svelte';
import { createPointerSessionManager } from './pointer-session.ts';

import {
	DRAG_DELETE_THRESHOLD,
	POSITION_PERCENT_STEP,
	formatPositionPercent,
	isValidPositionPercentDraft,
	serializeRampData
} from './shared.ts';

var root = $.from_html(`<div class="root svelte-1srt3l8"><div class="head svelte-1srt3l8"><input id="lut-editor-dialog-title" type="text" class="title-input svelte-1srt3l8"/> <div class="head-actions svelte-1srt3l8"><!> <!></div></div> <div class="body svelte-1srt3l8"><!> <div class="right-col svelte-1srt3l8"><!> <!></div></div></div>`);

const $$css = {
	hash: 'svelte-1srt3l8',
	code: '.root.svelte-1srt3l8 {display:flex;flex-direction:column;height:100%;}.head.svelte-1srt3l8 {display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 20px;border-bottom:1px solid var(--color-line);flex-shrink:0;}.title-input.svelte-1srt3l8 {min-width:0;flex:1;border:1px solid transparent;border-radius:8px;background:transparent;color:var(--color-text-strong);font-size:13px;font-weight:600;padding:4px 6px;margin:-4px 0 -4px -6px;}.title-input.svelte-1srt3l8:hover {border-color:color-mix(in srgb, var(--color-line), var(--color-accent) 32%);background:color-mix(in srgb, var(--color-panel-2), var(--color-bg) 10%);}.title-input.svelte-1srt3l8:focus {outline:none;border-color:var(--color-accent);background:color-mix(in srgb, var(--color-panel-2), var(--color-bg) 6%);}.head-actions.svelte-1srt3l8 {display:flex;gap:8px;align-items:center;}.body.svelte-1srt3l8 {display:flex;flex:1;overflow:hidden;}.right-col.svelte-1srt3l8 {display:flex;flex-direction:column;flex:1;overflow:hidden;}.lut-editor-menu-wrap {position:relative;}.lut-editor-menu-panel {min-width:180px;z-index:220;display:flex;flex-direction:column;gap:2px;padding:6px;border-radius:12px;border:1px solid var(--color-line);background:var(--color-panel);box-shadow:0 14px 28px color-mix(in srgb, black, transparent 78%);}\n\n  @media (max-width: 1120px) {.body.svelte-1srt3l8 {flex-direction:column;}\n  }'
};

export default function Svelte_lut_editor_dialog($$anchor, $$props) {
	$.push($$props, true);
	$.append_styles($$anchor, $$css);

	const rampData = $.prop($$props, 'rampData', 7, null),
		lutId = $.prop($$props, 'lutId', 7, null);

	function dispatch(eventName, detail) {
		$$props.$$host.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true, composed: true }));
	}

	let language = $.state($.proxy(getLanguage()));

	const disposeLanguageSync = subscribeLanguageChange((nextLanguage) => {
		$.set(language, nextLanguage, true);
	});

	let previewCanvasRef = $.state(null);
	let editingLutId = $.state(null);
	let selectedRampId = $.state(null);
	let focusedStopId = $.state(null);
	let initialSerializedRampData = $.state('');
	let editingRampPositionId = $.state(null);
	let rampPositionDraft = $.state('');
	let editingStopPositionId = $.state(null);
	let stopPositionDraft = $.state('');
	let draggingRampDeleteId = $.state(null);
	let draggingStopDeleteId = $.state(null);
	let draggingRampListIdx = $.state(null);
	let rafPendingId = $.state(null);
	let previewRevision = $.state(0);
	let previousSyncKey = $.state('');
	let lastDispatchedDirty = $.state(null);
	let editorRampData = $.state(null);
	const pointerSessionManager = createPointerSessionManager();

	onDestroy(() => {
		disposeLanguageSync();

		if ($.get(rafPendingId) !== null) {
			cancelAnimationFrame($.get(rafPendingId));
			$.set(rafPendingId, null);
		}

		pointerSessionManager.cleanupPointerSession();
	});

	function tr(key, values) {
		$.get(language);

		return values ? t(key, values) : t(key);
	}

	const syncKey = $.derived(() => `${lutId() ?? '__new__'}::${serializeRampData(rampData())}`);

	$.user_effect(() => {
		if ($.get(syncKey) === $.get(previousSyncKey)) {
			return;
		}

		$.set(previousSyncKey, $.get(syncKey), true);
		$.set(editingLutId, lutId());
		$.set(editorRampData, rampData());
		$.set(initialSerializedRampData, serializeRampData($.get(editorRampData)), true);
		$.set(selectedRampId, $.get(editorRampData)?.ramps[0]?.id ?? null, true);
		$.set(focusedStopId, $.get(editorRampData)?.ramps[0]?.stops[0]?.id ?? null, true);
		$.set(editingRampPositionId, null);
		$.set(rampPositionDraft, '');
		$.set(editingStopPositionId, null);
		$.set(stopPositionDraft, '');
		$.set(draggingRampDeleteId, null);
		$.set(draggingStopDeleteId, null);
		$.set(draggingRampListIdx, null);
		$.set(previewRevision, $.get(previewRevision) + 1);
	});

	const selectedRamp = $.derived(() => $.get(editorRampData) && $.get(selectedRampId)
		? $.get(editorRampData).ramps.find((ramp) => ramp.id === $.get(selectedRampId)) ?? null
		: null);

	const focusedStop = $.derived(() => $.get(selectedRamp) && $.get(focusedStopId)
		? $.get(selectedRamp).stops.find((stop) => stop.id === $.get(focusedStopId)) ?? null
		: null);

	const isDirty = $.derived(() => serializeRampData($.get(editorRampData)) !== $.get(initialSerializedRampData));

	$.user_effect(() => {
		if ($.get(isDirty) === $.get(lastDispatchedDirty)) {
			return;
		}

		$.set(lastDispatchedDirty, $.get(isDirty), true);
		dispatch('dirtychange', $.get(isDirty));
	});

	$.user_effect(() => {
		if (!$.get(selectedRamp)) {
			$.set(focusedStopId, null);
			$.set(editingRampPositionId, null);
			$.set(rampPositionDraft, '');

			return;
		}

		if ($.get(editingRampPositionId) !== $.get(selectedRamp).id) {
			$.set(rampPositionDraft, formatPositionPercent($.get(selectedRamp).position), true);
		}

		if (!$.get(focusedStopId) || !$.get(selectedRamp).stops.some((stop) => stop.id === $.get(focusedStopId))) {
			$.set(focusedStopId, $.get(selectedRamp).stops[0]?.id ?? null, true);
		}
	});

	$.user_effect(() => {
		if (!$.get(focusedStop)) {
			$.set(editingStopPositionId, null);
			$.set(stopPositionDraft, '');

			return;
		}

		if ($.get(editingStopPositionId) !== $.get(focusedStop).id) {
			$.set(stopPositionDraft, formatPositionPercent($.get(focusedStop).position), true);
		}
	});

	$.user_effect(() => {
		$.get(previewRevision);
		$.get(previewCanvasRef);
		$.get(editorRampData)?.axisSwap;
		$.get(editorRampData)?.width;
		$.get(editorRampData)?.height;
		$.get(selectedRamp)?.position;
		$.get(focusedStop)?.position;
		scheduleRedraw();
	});

	function handlePreviewCanvasRef(element) {
		$.set(previewCanvasRef, element, true);
		scheduleRedraw();
	}

	function scheduleRedraw() {
		if ($.get(rafPendingId) !== null) {
			return;
		}

		$.set(
			rafPendingId,
			requestAnimationFrame(() => {
				$.set(rafPendingId, null);
				redrawPreview();
			}),
			true
		);
	}

	function redrawPreview() {
		if (!$.get(editorRampData) || !$.get(previewCanvasRef)) {
			return;
		}

		const context = $.get(previewCanvasRef).getContext('2d');

		if (!context) {
			return;
		}

		const pixels = renderColorRamp2dToPixels($.get(editorRampData));
		const imageData = context.createImageData($.get(editorRampData).width, $.get(editorRampData).height);

		imageData.data.set(pixels);
		context.putImageData(imageData, 0, 0);

		const swapped = !!$.get(editorRampData).axisSwap;

		if ($.get(selectedRamp)) {
			const pos = Math.round($.get(selectedRamp).position * ((swapped
				? $.get(editorRampData).width
				: $.get(editorRampData).height) - 1));

			context.strokeStyle = 'rgba(255,255,255,0.5)';
			context.lineWidth = 1;
			context.setLineDash([3, 3]);
			context.beginPath();

			if (swapped) {
				context.moveTo(pos + 0.5, 0);
				context.lineTo(pos + 0.5, $.get(editorRampData).height);
			} else {
				context.moveTo(0, pos + 0.5);
				context.lineTo($.get(editorRampData).width, pos + 0.5);
			}

			context.stroke();
			context.setLineDash([]);
		}

		if ($.get(focusedStop)) {
			const pos = Math.round($.get(focusedStop).position * ((swapped
				? $.get(editorRampData).height
				: $.get(editorRampData).width) - 1));

			context.strokeStyle = 'rgba(255,255,255,0.5)';
			context.lineWidth = 1;
			context.setLineDash([3, 3]);
			context.beginPath();

			if (swapped) {
				context.moveTo(0, pos + 0.5);
				context.lineTo($.get(editorRampData).width, pos + 0.5);
			} else {
				context.moveTo(pos + 0.5, 0);
				context.lineTo(pos + 0.5, $.get(editorRampData).height);
			}

			context.stroke();
			context.setLineDash([]);
		}
	}

	function setNextRampData(nextData) {
		$.set(editorRampData, nextData);
		$.set(previewRevision, $.get(previewRevision) + 1);
	}

	function isRampBoundary(rampId) {
		if (!$.get(editorRampData)) {
			return false;
		}

		const index = $.get(editorRampData).ramps.findIndex((ramp) => ramp.id === rampId);

		return index === 0 || index === $.get(editorRampData).ramps.length - 1;
	}

	function isStopBoundary(stopId) {
		if (!$.get(selectedRamp)) {
			return false;
		}

		const index = $.get(selectedRamp).stops.findIndex((stop) => stop.id === stopId);

		return index === 0 || index === $.get(selectedRamp).stops.length - 1;
	}

	function canRemoveRamp(rampId) {
		if (!$.get(editorRampData) || $.get(editorRampData).ramps.length <= MIN_RAMPS) {
			return false;
		}

		const index = $.get(editorRampData).ramps.findIndex((ramp) => ramp.id === rampId);

		return index > 0 && index < $.get(editorRampData).ramps.length - 1;
	}

	function handleRemoveRamp(rampId) {
		if (!$.get(editorRampData)) {
			return;
		}

		const nextData = removeRamp($.get(editorRampData), rampId);

		setNextRampData(nextData);

		if ($.get(selectedRampId) === rampId) {
			$.set(selectedRampId, nextData.ramps[0]?.id ?? null, true);
		}
	}

	function handleAddRampAtY(position) {
		if (!$.get(editorRampData) || $.get(editorRampData).ramps.length >= MAX_RAMPS) {
			return;
		}

		const nextData = addRamp($.get(editorRampData), position);

		setNextRampData(nextData);

		const addedRamp = nextData.ramps.find((ramp) => !$.get(editorRampData)?.ramps.some((oldRamp) => oldRamp.id === ramp.id));

		if (addedRamp) {
			$.set(selectedRampId, addedRamp.id, true);
		}
	}

	function handleAddRamp() {
		if (!$.get(editorRampData) || $.get(editorRampData).ramps.length >= MAX_RAMPS) {
			return;
		}

		let bestGap = 0;
		let bestY = 0.5;

		for (let index = 0; index < $.get(editorRampData).ramps.length - 1; index += 1) {
			const currentRamp = $.get(editorRampData).ramps[index];
			const nextRamp = $.get(editorRampData).ramps[index + 1];

			if (!currentRamp || !nextRamp) {
				continue;
			}

			const gap = nextRamp.position - currentRamp.position;

			if (gap > bestGap) {
				bestGap = gap;
				bestY = (currentRamp.position + nextRamp.position) / 2;
			}
		}

		handleAddRampAtY(bestY);
	}

	function handleRemoveStop(stopId) {
		if (!$.get(editorRampData) || !$.get(selectedRamp)) {
			return;
		}

		const nextRamp = removeStop($.get(selectedRamp), stopId);

		setNextRampData(updateRamp($.get(editorRampData), nextRamp));

		if ($.get(focusedStopId) === stopId) {
			$.set(focusedStopId, nextRamp.stops[0]?.id ?? null, true);
		}
	}

	function handleAddStopAtPos(position) {
		if (!$.get(editorRampData) || !$.get(selectedRamp) || $.get(selectedRamp).stops.length >= MAX_STOPS_PER_RAMP) {
			return;
		}

		const nextRamp = addStop($.get(selectedRamp), position);

		setNextRampData(updateRamp($.get(editorRampData), nextRamp));

		const addedStop = nextRamp.stops.find((stop) => !$.get(selectedRamp)?.stops.some((oldStop) => oldStop.id === stop.id));

		if (addedStop) {
			$.set(focusedStopId, addedStop.id, true);
		}
	}

	function handleAddStop() {
		if (!$.get(selectedRamp)) {
			return;
		}

		let bestGap = 0;
		let bestPos = 0.5;

		for (let index = 0; index < $.get(selectedRamp).stops.length - 1; index += 1) {
			const currentStop = $.get(selectedRamp).stops[index];
			const nextStop = $.get(selectedRamp).stops[index + 1];

			if (!currentStop || !nextStop) {
				continue;
			}

			const gap = nextStop.position - currentStop.position;

			if (gap > bestGap) {
				bestGap = gap;
				bestPos = (currentStop.position + nextStop.position) / 2;
			}
		}

		handleAddStopAtPos(bestPos);
	}

	function handleStopColorChange(stopId, hexValue) {
		if (!$.get(editorRampData) || !$.get(selectedRamp)) {
			return;
		}

		const color = parseHexColor(hexValue);

		if (!color) {
			return;
		}

		setNextRampData(updateRamp($.get(editorRampData), updateStopColor($.get(selectedRamp), stopId, color)));
	}

	function handleStopAlphaChange(stopId, value) {
		if (!$.get(editorRampData) || !$.get(selectedRamp)) {
			return;
		}

		setNextRampData(updateRamp($.get(editorRampData), updateStopAlpha($.get(selectedRamp), stopId, Number(value) / 100)));
	}

	function beginRampPositionEdit(rampId, currentDraft) {
		$.set(editingRampPositionId, rampId, true);
		$.set(rampPositionDraft, currentDraft, true);
	}

	function cancelRampPositionEdit(currentDraft) {
		$.set(editingRampPositionId, null);
		$.set(rampPositionDraft, currentDraft, true);
	}

	function handleRampPositionChange(rampId, percentValue) {
		if (!$.get(editorRampData) || !isValidPositionPercentDraft(percentValue)) {
			return;
		}

		$.set(rampPositionDraft, percentValue, true);

		if (percentValue === '' || percentValue === '.') {
			return;
		}

		const parsed = Number(percentValue);

		if (!Number.isFinite(parsed)) {
			return;
		}

		setNextRampData(moveRamp($.get(editorRampData), rampId, parsed / 100));
	}

	function commitRampPositionDraft(rampId) {
		const ramp = $.get(editorRampData)?.ramps.find((item) => item.id === rampId) ?? $.get(selectedRamp);
		const parsed = Number($.get(rampPositionDraft));

		$.set(editingRampPositionId, null);

		if (!ramp || !Number.isFinite(parsed)) {
			$.set(rampPositionDraft, ramp ? formatPositionPercent(ramp.position) : '', true);

			return;
		}

		const normalized = formatPositionPercent(parsed / 100);

		handleRampPositionChange(rampId, normalized);
		$.set(rampPositionDraft, normalized, true);
	}

	function handleRampPositionWheel(rampId, currentPosition, event) {
		event.preventDefault();

		const delta = event.deltaY < 0
			? POSITION_PERCENT_STEP / 100
			: -POSITION_PERCENT_STEP / 100;

		handleRampPositionChange(rampId, formatPositionPercent(Math.max(0, Math.min(1, currentPosition + delta))));
	}

	function beginStopPositionEdit(stopId, currentDraft) {
		$.set(editingStopPositionId, stopId, true);
		$.set(stopPositionDraft, currentDraft, true);
	}

	function cancelStopPositionEdit(currentDraft) {
		$.set(editingStopPositionId, null);
		$.set(stopPositionDraft, currentDraft, true);
	}

	function handleStopPositionChange(stopId, percentValue) {
		if (!$.get(editorRampData) || !$.get(selectedRamp) || !isValidPositionPercentDraft(percentValue)) {
			return;
		}

		$.set(stopPositionDraft, percentValue, true);

		if (percentValue === '' || percentValue === '.') {
			return;
		}

		const parsed = Number(percentValue);

		if (!Number.isFinite(parsed)) {
			return;
		}

		setNextRampData(updateRamp($.get(editorRampData), moveStop($.get(selectedRamp), stopId, parsed / 100)));
	}

	function commitStopPositionDraft(stopId) {
		const parsed = Number($.get(stopPositionDraft));

		$.set(editingStopPositionId, null);

		if (!$.get(focusedStop) || $.get(focusedStop).id !== stopId || !Number.isFinite(parsed)) {
			$.set(
				stopPositionDraft,
				$.get(focusedStop)
					? formatPositionPercent($.get(focusedStop).position)
					: '',
				true
			);

			return;
		}

		const normalized = formatPositionPercent(parsed / 100);

		handleStopPositionChange(stopId, normalized);
		$.set(stopPositionDraft, normalized, true);
	}

	function handleStopPositionWheel(stopId, currentPosition, event) {
		event.preventDefault();

		const delta = event.deltaY < 0
			? POSITION_PERCENT_STEP / 100
			: -POSITION_PERCENT_STEP / 100;

		handleStopPositionChange(stopId, formatPositionPercent(Math.max(0, Math.min(1, currentPosition + delta))));
	}

	function handleStopAlphaWheel(stopId, currentAlpha, event) {
		event.preventDefault();

		const delta = event.deltaY < 0 ? 0.01 : -0.01;

		handleStopAlphaChange(stopId, String(Math.round(Math.max(0, Math.min(1, currentAlpha + delta)) * 100)));
	}

	function getDuplicatePosition(positions, index) {
		const current = positions[index];

		if (current === undefined) {
			return null;
		}

		const prevGap = index > 0 ? current - positions[index - 1] : -1;
		const nextGap = index < positions.length - 1 ? positions[index + 1] - current : -1;

		if (nextGap > 0 && (prevGap <= 0 || nextGap >= prevGap)) {
			return current + nextGap / 2;
		}

		if (prevGap > 0) {
			return current - prevGap / 2;
		}

		return null;
	}

	function handleDuplicateSelectedRamp() {
		if (!$.get(editorRampData) || !$.get(selectedRamp) || $.get(editorRampData).ramps.length >= MAX_RAMPS) {
			return;
		}

		const rampIndex = $.get(editorRampData).ramps.findIndex((candidate) => candidate.id === $.get(selectedRamp).id);

		if (rampIndex < 0) {
			return;
		}

		const duplicatePosition = getDuplicatePosition($.get(editorRampData).ramps.map((candidate) => candidate.position), rampIndex);

		if (duplicatePosition === null) {
			return;
		}

		const duplicatedRamp = {
			id: uid('ramp'),
			position: duplicatePosition,
			stops: $.get(selectedRamp).stops.map((stop) => ({ ...stop, id: uid('stop') }))
		};

		const nextData = {
			...$.get(editorRampData),
			ramps: [...$.get(editorRampData).ramps, duplicatedRamp].sort((a, b) => a.position - b.position)
		};

		setNextRampData(nextData);
		$.set(selectedRampId, duplicatedRamp.id, true);
		$.set(focusedStopId, duplicatedRamp.stops[0]?.id ?? null, true);
	}

	function handleInvertSelectedRamp() {
		if (!$.get(editorRampData) || !$.get(selectedRamp)) {
			return;
		}

		const selectedId = $.get(selectedRamp).id;
		const currentFocusedStopId = $.get(focusedStopId);
		const invertedRamps = [...$.get(editorRampData).ramps].reverse().map((ramp) => ({ ...ramp, position: 1 - ramp.position })).sort((a, b) => a.position - b.position);

		setNextRampData({ ...$.get(editorRampData), ramps: invertedRamps });
		$.set(selectedRampId, selectedId, true);
		$.set(focusedStopId, currentFocusedStopId, true);
	}

	function handleDuplicateFocusedStop() {
		if (!$.get(editorRampData) || !$.get(selectedRamp) || !$.get(focusedStop) || $.get(selectedRamp).stops.length >= MAX_STOPS_PER_RAMP) {
			return;
		}

		const stopIndex = $.get(selectedRamp).stops.findIndex((candidate) => candidate.id === $.get(focusedStop).id);

		if (stopIndex < 0) {
			return;
		}

		const duplicatePosition = getDuplicatePosition($.get(selectedRamp).stops.map((candidate) => candidate.position), stopIndex);

		if (duplicatePosition === null) {
			return;
		}

		const duplicatedStop = {
			...$.get(focusedStop),
			id: uid('stop'),
			position: duplicatePosition
		};

		const updatedRamp = {
			...$.get(selectedRamp),
			stops: [...$.get(selectedRamp).stops, duplicatedStop].sort((a, b) => a.position - b.position)
		};

		setNextRampData(updateRamp($.get(editorRampData), updatedRamp));
		$.set(focusedStopId, duplicatedStop.id, true);
	}

	function handleInvertFocusedStop() {
		if (!$.get(editorRampData) || !$.get(selectedRamp) || !$.get(focusedStop)) {
			return;
		}

		const updatedRamp = {
			...$.get(selectedRamp),
			stops: [...$.get(selectedRamp).stops].reverse().map((stop) => ({ ...stop, position: 1 - stop.position })).sort((a, b) => a.position - b.position)
		};

		setNextRampData(updateRamp($.get(editorRampData), updatedRamp));
		$.set(focusedStopId, $.get(focusedStop).id, true);
	}

	function startRampDrag(rampId, event, stripEl) {
		event.preventDefault();

		let pendingDelete = false;

		const onMove = (moveEvent) => {
			if (!stripEl) {
				return;
			}

			const rect = stripEl.getBoundingClientRect();
			const swapped = !!$.get(editorRampData)?.axisSwap;

			const newPosition = swapped
				? Math.max(0, Math.min(1, (moveEvent.clientX - rect.left) / rect.width))
				: Math.max(0, Math.min(1, (moveEvent.clientY - rect.top) / rect.height));

			const deleteDistance = swapped
				? moveEvent.clientY - rect.bottom
				: moveEvent.clientX - rect.right;

			const nowDelete = !isRampBoundary(rampId) && deleteDistance > DRAG_DELETE_THRESHOLD;

			pendingDelete = nowDelete;
			$.set(draggingRampDeleteId, nowDelete ? rampId : null, true);

			if (!nowDelete && $.get(editorRampData)) {
				setNextRampData(moveRamp($.get(editorRampData), rampId, newPosition));
			}
		};

		const onUp = () => {
			$.set(draggingRampDeleteId, null);

			if (pendingDelete) {
				handleRemoveRamp(rampId);
			}
		};

		pointerSessionManager.beginPointerSession(onMove, onUp);
	}

	function startStopDrag(stopId, event, stripEl) {
		event.preventDefault();

		let pendingDelete = false;

		const onMove = (moveEvent) => {
			if (!stripEl) {
				return;
			}

			const rect = stripEl.getBoundingClientRect();
			const swapped = !!$.get(editorRampData)?.axisSwap;

			const newPosition = swapped
				? Math.max(0, Math.min(1, (moveEvent.clientY - rect.top) / rect.height))
				: Math.max(0, Math.min(1, (moveEvent.clientX - rect.left) / rect.width));

			const deleteDistance = swapped
				? moveEvent.clientX - rect.right
				: moveEvent.clientY - rect.bottom;

			const nowDelete = !isStopBoundary(stopId) && deleteDistance > DRAG_DELETE_THRESHOLD;

			pendingDelete = nowDelete;
			$.set(draggingStopDeleteId, nowDelete ? stopId : null, true);

			if (!nowDelete && $.get(editorRampData) && $.get(selectedRamp)) {
				setNextRampData(updateRamp($.get(editorRampData), moveStop($.get(selectedRamp), stopId, newPosition)));
			}
		};

		const onUp = () => {
			$.set(draggingStopDeleteId, null);

			if (pendingDelete) {
				handleRemoveStop(stopId);
			}
		};

		pointerSessionManager.beginPointerSession(onMove, onUp);
	}

	function startPreviewStopDrag(stopId, event, previewBarEl) {
		event.preventDefault();

		const onMove = (moveEvent) => {
			if (!previewBarEl || !$.get(editorRampData) || !$.get(selectedRamp)) {
				return;
			}

			const rect = previewBarEl.getBoundingClientRect();
			const newPosition = Math.max(0, Math.min(1, (moveEvent.clientX - rect.left) / rect.width));

			setNextRampData(updateRamp($.get(editorRampData), moveStop($.get(selectedRamp), stopId, newPosition)));
		};

		pointerSessionManager.beginPointerSession(onMove, () => undefined);
	}

	function handleRampStripPointerDown(event, stripEl) {
		if (!stripEl || event.target !== stripEl) {
			return;
		}

		const rect = stripEl.getBoundingClientRect();
		const swapped = !!$.get(editorRampData)?.axisSwap;

		const position = swapped
			? Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
			: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));

		handleAddRampAtY(position);
	}

	function handleStopStripPointerDown(event, stripEl) {
		if (!stripEl || event.target !== stripEl) {
			return;
		}

		const rect = stripEl.getBoundingClientRect();
		const swapped = !!$.get(editorRampData)?.axisSwap;

		const position = swapped
			? Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
			: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));

		handleAddStopAtPos(position);
	}

	function handleCanvasClick(event, canvasEl) {
		if (!$.get(editorRampData) || !canvasEl) {
			return;
		}

		const rect = canvasEl.getBoundingClientRect();
		const relX = (event.clientX - rect.left) / rect.width;
		const relY = (event.clientY - rect.top) / rect.height;
		const swapped = !!$.get(editorRampData).axisSwap;
		const rampAxis = swapped ? relX : relY;
		const stopAxis = swapped ? relY : relX;
		let nearestRamp = null;
		let bestRampDistance = Infinity;

		for (const ramp of $.get(editorRampData).ramps) {
			const distance = Math.abs(ramp.position - rampAxis);

			if (distance < bestRampDistance) {
				bestRampDistance = distance;
				nearestRamp = ramp;
			}
		}

		if (!nearestRamp) {
			return;
		}

		$.set(selectedRampId, nearestRamp.id, true);

		let nearestStop = null;
		let bestStopDistance = Infinity;

		for (const stop of nearestRamp.stops) {
			const distance = Math.abs(stop.position - stopAxis);

			if (distance < bestStopDistance) {
				bestStopDistance = distance;
				nearestStop = stop;
			}
		}

		if (nearestStop) {
			$.set(focusedStopId, nearestStop.id, true);
		}
	}

	const rampReorderController = createPointerReorderListController({
		beginPointerSession: pointerSessionManager.beginPointerSession,
		getItems: () => $.get(editorRampData)?.ramps ?? [],
		queryCandidateElements: () => Array.from(document.querySelectorAll('.ramp-list .ramp-row[data-ramp-id]')),
		getElementItemId: (element) => element.dataset.rampId ?? null,
		axis: 'vertical',
		getPointerCoord: (event) => event.clientY,
		setDraggingIndex: (index) => {
			$.set(draggingRampListIdx, index, true);
		},

		setDropTarget: (targetId, after) => {
			syncReorderDropIndicators({
				elements: Array.from(document.querySelectorAll('.ramp-list .ramp-row[data-ramp-id]')),
				getElementItemId: (element) => element.dataset.rampId ?? null,
				dropTargetId: targetId,
				dropAfter: after
			});
		},

		commitMove: (fromIndex, insertBeforeIndex) => {
			if (!$.get(editorRampData)) {
				return;
			}

			const nextData = reorderRamps($.get(editorRampData), fromIndex, insertBeforeIndex);

			if (nextData !== $.get(editorRampData)) {
				setNextRampData(nextData);
			}
		}
	});

	function handleApply() {
		if (!$.get(editorRampData)) {
			return;
		}

		const nextLut = createLutFromColorRamp2d($.get(editorRampData));

		$.set(initialSerializedRampData, serializeRampData($.get(editorRampData)), true);
		dispatch('apply-lut', { lutId: $.get(editingLutId), updatedLut: nextLut });
		dispatch('request-close');
	}

	function rampSwatchStyle(ramp) {
		const stops = ramp.stops.map((stop) => `${colorToHex(stop.color)} ${Math.round(stop.position * 100)}%`);

		return `background: linear-gradient(to right, ${stops.join(', ')})`;
	}

	var $$exports = {
		get rampData() {
			return rampData();
		},

		set rampData($$value = null) {
			rampData($$value);
			$.flush();
		},

		get lutId() {
			return lutId();
		},

		set lutId($$value = null) {
			lutId($$value);
			$.flush();
		}
	};

	var fragment = $.comment();
	var node = $.first_child(fragment);

	$.key(node, () => $.get(language), ($$anchor) => {
		var div = root();
		var div_1 = $.child(div);
		var input = $.child(div_1);

		$.remove_input_defaults(input);

		var div_2 = $.sibling(input, 2);
		var node_1 = $.child(div_2);

		{
			let $0 = $.derived(() => !$.get(editorRampData));

			Button(node_1, {
				variant: 'submit',
				handlePress: handleApply,
				get disabled() {
					return $.get($0);
				},

				children: ($$anchor, $$slotProps) => {
					$.next();

					var text = $.text();

					$.template_effect(($0) => $.set_text(text, $0), [() => tr('lutEditor.apply')]);
					$.append($$anchor, text);
				},
				$$slots: { default: true }
			});
		}

		var node_2 = $.sibling(node_1, 2);

		Button(node_2, {
			variant: 'secondary',
			handlePress: () => dispatch('request-close'),
			children: ($$anchor, $$slotProps) => {
				$.next();

				var text_1 = $.text();

				$.template_effect(($0) => $.set_text(text_1, $0), [() => tr('lutEditor.cancel')]);
				$.append($$anchor, text_1);
			},
			$$slots: { default: true }
		});

		$.reset(div_2);
		$.reset(div_1);

		var div_3 = $.sibling(div_1, 2);
		var node_3 = $.child(div_3);

		{
			let $0 = $.derived(() => tr('lutEditor.rampRailHint'));
			let $1 = $.derived(() => tr('lutEditor.stopRailHint'));
			let $2 = $.derived(() => tr('lutEditor.axisStopXRampY'));
			let $3 = $.derived(() => tr('lutEditor.axisRampXStopY'));

			PreviewPane(node_3, {
				get editorRampData() {
					return $.get(editorRampData);
				},

				get selectedRampId() {
					return $.get(selectedRampId);
				},

				get focusedStopId() {
					return $.get(focusedStopId);
				},

				get selectedRamp() {
					return $.get(selectedRamp);
				},

				get draggingRampDeleteId() {
					return $.get(draggingRampDeleteId);
				},

				get draggingStopDeleteId() {
					return $.get(draggingStopDeleteId);
				},

				get rampRailHint() {
					return $.get($0);
				},

				get stopRailHint() {
					return $.get($1);
				},

				get axisStopXRampYLabel() {
					return $.get($2);
				},

				get axisRampXStopYLabel() {
					return $.get($3);
				},
				isRampBoundary,
				isStopBoundary,
				onCanvasRef: handlePreviewCanvasRef,
				onCanvasClick: handleCanvasClick,
				onRampStripPointerDown: handleRampStripPointerDown,
				onStopStripPointerDown: handleStopStripPointerDown,
				onRampKnobPointerDown: startRampDrag,
				onStopKnobPointerDown: startStopDrag,
				onAxisSwapChange: (nextAxisSwap) => {
					if ($.get(editorRampData)) {
						setNextRampData({ ...$.get(editorRampData), axisSwap: nextAxisSwap });
					}
				},

				onSelectRamp: (rampId) => {
					$.set(selectedRampId, rampId, true);
				},

				onSelectStop: (stopId) => {
					$.set(focusedStopId, stopId, true);
				}
			});
		}

		var div_4 = $.sibling(node_3, 2);
		var node_4 = $.child(div_4);

		{
			let $0 = $.derived(() => $.get(editorRampData)?.ramps ?? []);
			let $1 = $.derived(() => tr('lutEditor.rampListLabel'));
			let $2 = $.derived(() => tr('lutEditor.addRamp'));
			let $3 = $.derived(() => tr('lutEditor.duplicateRamp'));
			let $4 = $.derived(() => tr('lutEditor.invertRamp'));
			let $5 = $.derived(() => tr('lutEditor.removeRamp'));
			let $6 = $.derived(() => tr('lutEditor.rampPosition'));
			let $7 = $.derived(() => tr('lutEditor.rampMenuAria'));
			let $8 = $.derived(() => !$.get(editorRampData) || ($.get(editorRampData)?.ramps.length ?? 0) >= MAX_RAMPS);

			RampSection(node_4, {
				get ramps() {
					return $.get($0);
				},

				get selectedRamp() {
					return $.get(selectedRamp);
				},

				get selectedRampId() {
					return $.get(selectedRampId);
				},

				get editingRampPositionId() {
					return $.get(editingRampPositionId);
				},

				get rampPositionDraft() {
					return $.get(rampPositionDraft);
				},

				get draggingRampListIdx() {
					return $.get(draggingRampListIdx);
				},

				get rampListLabel() {
					return $.get($1);
				},

				get addRampLabel() {
					return $.get($2);
				},

				get duplicateRampLabel() {
					return $.get($3);
				},

				get invertRampLabel() {
					return $.get($4);
				},

				get removeRampLabel() {
					return $.get($5);
				},

				get rampPositionLabel() {
					return $.get($6);
				},

				get rampMenuAriaLabel() {
					return $.get($7);
				},

				get maxRampsReached() {
					return $.get($8);
				},
				canRemoveRamp,
				rampSwatchStyle,
				onAddRamp: handleAddRamp,
				onSelectRamp: (rampId) => {
					$.set(selectedRampId, rampId, true);
				},

				get onStartRampListDrag() {
					return rampReorderController.startDrag;
				},
				onRemoveRamp: handleRemoveRamp,
				onDuplicateSelectedRamp: handleDuplicateSelectedRamp,
				onInvertSelectedRamp: handleInvertSelectedRamp,
				onBeginRampPositionEdit: beginRampPositionEdit,
				onRampPositionInput: handleRampPositionChange,
				onCommitRampPosition: commitRampPositionDraft,
				onCancelRampPositionEdit: cancelRampPositionEdit,
				onRampPositionWheel: handleRampPositionWheel,
				get shouldSuppressRampClick() {
					return rampReorderController.shouldSuppressClick;
				}
			});
		}

		var node_5 = $.sibling(node_4, 2);

		{
			let $0 = $.derived(() => tr('lutEditor.stopEditorLabel'));
			let $1 = $.derived(() => tr('lutEditor.addStop'));
			let $2 = $.derived(() => tr('lutEditor.removeStop'));
			let $3 = $.derived(() => tr('lutEditor.duplicateStop'));
			let $4 = $.derived(() => tr('lutEditor.invertStop'));
			let $5 = $.derived(() => tr('lutEditor.stopMenuAria'));
			let $6 = $.derived(() => tr('lutEditor.stopColor'));
			let $7 = $.derived(() => tr('lutEditor.stopPosition'));
			let $8 = $.derived(() => tr('lutEditor.alpha'));
			let $9 = $.derived(() => tr('lutEditor.noStopSelected'));
			let $10 = $.derived(() => !$.get(selectedRamp) || ($.get(selectedRamp)?.stops.length ?? 0) >= MAX_STOPS_PER_RAMP);

			StopSection(node_5, {
				get selectedRamp() {
					return $.get(selectedRamp);
				},

				get focusedStop() {
					return $.get(focusedStop);
				},

				get stopPositionDraft() {
					return $.get(stopPositionDraft);
				},

				get editingStopPositionId() {
					return $.get(editingStopPositionId);
				},

				get stopEditorLabel() {
					return $.get($0);
				},

				get addStopLabel() {
					return $.get($1);
				},

				get removeStopLabel() {
					return $.get($2);
				},

				get duplicateStopLabel() {
					return $.get($3);
				},

				get invertStopLabel() {
					return $.get($4);
				},

				get stopMenuAriaLabel() {
					return $.get($5);
				},

				get stopColorLabel() {
					return $.get($6);
				},

				get stopPositionLabel() {
					return $.get($7);
				},

				get alphaLabel() {
					return $.get($8);
				},

				get noStopSelectedLabel() {
					return $.get($9);
				},

				get maxStopsReached() {
					return $.get($10);
				},
				isStopBoundary,
				onAddStop: handleAddStop,
				onRemoveStop: handleRemoveStop,
				onDuplicateFocusedStop: handleDuplicateFocusedStop,
				onInvertFocusedStop: handleInvertFocusedStop,
				onSelectStop: (stopId) => {
					$.set(focusedStopId, stopId, true);
				},
				onPreviewStopPointerDown: startPreviewStopDrag,
				onStopColorInput: handleStopColorChange,
				onBeginStopPositionEdit: beginStopPositionEdit,
				onStopPositionInput: handleStopPositionChange,
				onCommitStopPosition: commitStopPositionDraft,
				onCancelStopPositionEdit: cancelStopPositionEdit,
				onStopPositionWheel: handleStopPositionWheel,
				onStopAlphaInput: handleStopAlphaChange,
				onStopAlphaWheel: handleStopAlphaWheel
			});
		}

		$.reset(div_4);
		$.reset(div_3);
		$.reset(div);
		$.template_effect(() => $.set_value(input, $.get(editorRampData)?.name ?? ''));

		$.delegated('input', input, (event) => {
			const name = event.currentTarget.value;

			if ($.get(editorRampData)) {
				setNextRampData({ ...$.get(editorRampData), name });
			}
		});

		$.append($$anchor, div);
	});

	$.append($$anchor, fragment);

	return $.pop($$exports);
}

$.delegate(['input']);
customElements.define('lut-lut-editor-dialog-content', $.create_custom_element(Svelte_lut_editor_dialog, { rampData: {}, lutId: {} }, [], []));